import posthog from "posthog-js";

// PostHog (EU region) — analytics, consent-gated. Nothing is sent until the user
// explicitly accepts via the cookie banner. The project key is a public client
// ingestion key (safe in the bundle); the host defaults to the EU cloud.
const KEY = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
const HOST = process.env.REACT_APP_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com";
const CONSENT_KEY = "provena_cookie_consent";

let initialized = false;

// "granted" | "denied" | null (no decision yet → banner should show).
export function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw).analytics ?? null;
  } catch {
    return null;
  }
}

function storeConsent(value) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ analytics: value, at: new Date().toISOString() })
    );
  } catch {
    /* localStorage unavailable */
  }
}

// Initialise PostHog with capturing OPTED-OUT by default. Web Analytics
// (pageviews, UTM, referrer) and custom events only flow after grantConsent().
export function initAnalytics() {
  if (initialized || !KEY) return; // no key (e.g. local dev) → no-op
  posthog.init(KEY, {
    api_host: HOST,
    opt_out_capturing_by_default: true,
    // `true` ne compte que le chargement initial : la classe qui écoute
    // pushState / replaceState et les boutons précédent-suivant ne s'active
    // qu'avec cette valeur. L'application étant une SPA, tout ce qui suivait la
    // première page était invisible, et un parcours interne n'apparaissait que
    // sous forme de $web_vitals orphelins.
    capture_pageview: "history_change",
    capture_pageleave: true,
    autocapture: false, // explicit events only — avoids capturing form inputs
    // Heatmaps: click / rageclick / scroll positional data for the PostHog
    // Heatmaps tool. Coordinates only — never field contents (autocapture stays
    // off). Still consent-gated (opted out until the banner is accepted).
    enable_heatmaps: true,
    persistence: "localStorage+cookie",
  });
  initialized = true;
  // Returning visitor who already accepted → opt back in at load.
  if (getStoredConsent() === "granted") posthog.opt_in_capturing();
}

export function grantConsent() {
  storeConsent("granted");
  if (initialized) posthog.opt_in_capturing();
}

export function revokeConsent() {
  storeConsent("denied");
  if (initialized) posthog.opt_out_capturing();
}

// Safe capture wrapper — no-op when PostHog is uninitialised or opted out.
// Renvoie `false` quand rien n'a été émis, ce dont captureOnce a besoin pour ne
// pas poser un verrou définitif sur un événement qui n'est jamais parti.
export function capture(event, properties) {
  if (!initialized || posthog.has_opted_out_capturing()) return false;
  posthog.capture(event, properties);
  return true;
}

/**
 * Rattache l'activité anonyme déjà enregistrée au compte qui vient d'être
 * reconnu. PostHog fusionne alors l'identifiant anonyme du visiteur avec
 * `userId` : la visite qui a précédé l'inscription reste attribuée au bon
 * utilisateur, ce qui est la condition pour que les entonnoirs
 * « visite -> inscription » soient justes.
 *
 * Idempotent : PostHog ignore un identify() répété sur le même identifiant,
 * on peut donc l'appeler à chaque résolution de session sans précaution.
 *
 * Aucune donnée personnelle n'est envoyée comme propriété de personne. Le
 * projet s'interdit déjà d'attacher nom, e-mail ou numéro de série aux
 * événements ; l'identifiant de compte suffit à relier les sessions, et le
 * reste ne décrit que le type de compte.
 */
export function identify(user) {
  if (!initialized || !user?.user_id) return;
  posthog.identify(user.user_id, {
    actor_type: user.actor_type,
    pro_status: user.pro_status,
    is_admin: user.is_admin,
  });
}

// À la déconnexion : casse le lien avec le compte et repart sur un identifiant
// anonyme neuf. Sans cela, la session du visiteur suivant sur le même appareil
// serait attribuée au compte précédent.
//
// `reset()` efface aussi l'état de consentement, et la bibliothèque revient
// alors à son défaut, ici `opt_out_capturing_by_default`, donc désactivée.
// Sa propre documentation le dit : « calling reset() after opt_in_capturing()
// silently stops capturing. Always reset() first, then opt in. » Sans la ligne
// ci-dessous, plus rien n'était mesuré entre une déconnexion et le rechargement
// suivant, alors que la personne avait consenti et que ce consentement est
// toujours stocké. On le rétablit donc immédiatement, dans cet ordre.
export function resetIdentity() {
  if (!initialized) return;
  posthog.reset();
  if (getStoredConsent() === "granted") posthog.opt_in_capturing();
}
